/**
 * Gera uma senha temporária forte e LEGÍVEL — puro, testável sem tela.
 *
 * Legível importa mais do que parece: o gestor vai ditar ou copiar essa senha
 * para a colaboradora, muitas vezes por telefone. Uma senha com `l`, `1`, `O` e
 * `0` misturados vira uma sequência de tentativas frustradas no login.
 *
 * Por isso o alfabeto exclui os pares que se confundem — `0/O`, `1/l/I`, `5/S` —
 * e o formato é em blocos separados por hífen, que se lê em voz alta sem perder
 * a conta.
 *
 * É senha TEMPORÁRIA: a pessoa troca depois. O objetivo é atravessar o dia sem
 * travar o atendimento, não ser o segredo definitivo.
 */

/**
 * Sem os caracteres que se confundem lidos ou ditados — os DOIS lados de cada par.
 *
 * `i`, `l` e `I` saem por causa do `1`; `o` e `O`, por causa do `0`; e o **`5`
 * sai por causa do `s`/`S`**, que ficam. Excluir só um lado do par não resolve
 * nada — foi o que eu tinha feito na primeira versão, e o teste pegou.
 */
const LETRAS = 'abcdefghjkmnpqrstuvwxyz';
const MAIUSCULAS = 'ABCDEFGHJKMNPQRSTUVWXYZ';
const DIGITOS = '2346789';

const sortear = (alfabeto, quantos) => {
  // `crypto.getRandomValues` e não `Math.random`: é senha, ainda que temporária.
  const valores = new Uint32Array(quantos);
  crypto.getRandomValues(valores);
  return [...valores].map((v) => alfabeto[v % alfabeto.length]).join('');
};

/**
 * Devolve algo como `Kfmp-7rqt-94`.
 *
 * 12 caracteres úteis, bem acima do mínimo de 8 que o servidor exige, e com
 * maiúscula e dígito garantidos — não por exigência da validação (que só olha o
 * tamanho), mas para a senha não sair fraca por sorteio.
 */
export function gerarSenhaTemporaria() {
  const bloco1 = sortear(MAIUSCULAS, 1) + sortear(LETRAS, 3);
  const bloco2 = sortear(LETRAS, 4);
  const bloco3 = sortear(DIGITOS, 4);
  return `${bloco1}-${bloco2}-${bloco3}`;
}

/** Mínimo que o servidor aceita (`guardas.js`), repetido aqui para avisar antes. */
export const TAMANHO_MINIMO_SENHA = 8;

/**
 * `null` quando serve; a mensagem de recusa quando não.
 *
 * A validação acontece duas vezes de propósito: aqui para dar mensagem clara, e
 * no servidor, que é a autoridade.
 */
export function recusarSenha(valor) {
  const v = String(valor ?? '');
  if (!v.trim()) return 'Digite a senha nova ou clique em Gerar.';
  if (v.length < TAMANHO_MINIMO_SENHA) {
    return `A senha precisa ter ao menos ${TAMANHO_MINIMO_SENHA} caracteres.`;
  }
  if (/\s/.test(v)) return 'A senha não pode ter espaços.';
  return null;
}
